import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, getSession, logout, type SessionInfo } from './api.js';
import { AUTH_MODE, isBearerAuthEnabled, isCookieAuthEnabled } from './auth-config.js';

const SESSION_QUERY_KEY = ['auth', 'session'] as const;

/**
 * What the dashboard should do about session state.
 *
 *   - disabled        : bearer-only mode; no /auth/me request; mount app.
 *   - loading         : cookie/hybrid mode, /auth/me in flight; render
 *                       a tiny placeholder.
 *   - unauthenticated : cookie mode and /auth/me returned 401; show
 *                       the LoginScreen. Hybrid mode demotes to
 *                       `bearer_fallback` instead when a bearer is
 *                       configured.
 *   - bearer_fallback : hybrid mode, /auth/me returned 401, bearer is
 *                       configured. Mount the app and let API calls
 *                       go through with `Authorization: Bearer …`.
 *   - authenticated   : cookie mode and /auth/me returned 200.
 */
export type SessionState =
  | { status: 'disabled' }
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'bearer_fallback' }
  | { status: 'authenticated'; operatorId: string; expiresAt: number };

export interface UseSessionReturn {
  state: SessionState;
  /** Re-run /auth/me; use after login or logout. */
  refresh: () => Promise<void>;
  /** POST /auth/logout, then refresh. */
  signOut: () => Promise<void>;
}

/**
 * Hook around GET /auth/me. In bearer-only mode it returns
 * {status:'disabled'} without making a request.
 *
 * The session info never includes secrets — only the operator id and the
 * cookie's expiry timestamp. Both are safe to render.
 */
export function useSession(): UseSessionReturn {
  const qc = useQueryClient();
  const cookieEnabled = isCookieAuthEnabled();

  const query = useQuery<SessionInfo | null, ApiError>({
    queryKey: SESSION_QUERY_KEY,
    enabled: cookieEnabled,
    staleTime: 60_000,
    retry: false,
    queryFn: async () => {
      try {
        return await getSession();
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
  });

  let state: SessionState;
  if (!cookieEnabled) state = { status: 'disabled' };
  else if (query.isPending) state = { status: 'loading' };
  else if (query.data) {
    state = {
      status: 'authenticated',
      operatorId: query.data.operator_id,
      expiresAt: query.data.expires_at,
    };
  } else if (AUTH_MODE === 'hybrid' && isBearerAuthEnabled('hybrid')) {
    // Hybrid means "try cookie first, fall back to bearer". A 401 from
    // /auth/me is the cookie path saying no — but if a bearer is
    // configured, normal API calls will still succeed. Mount the app.
    // Cookie mode never lands here because it short-circuits above.
    state = { status: 'bearer_fallback' };
  } else {
    state = { status: 'unauthenticated' };
  }

  return {
    state,
    async refresh() {
      if (!cookieEnabled) return;
      await qc.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    },
    async signOut() {
      if (!cookieEnabled) return;
      try {
        await logout();
      } catch {
        // Best effort: even if the network call fails, drop local state.
      }
      qc.setQueryData(SESSION_QUERY_KEY, null);
      // Also drop any cached data tied to the previous session.
      qc.removeQueries({ queryKey: ['threads'] });
      qc.removeQueries({ queryKey: ['thread'] });
    },
  };
}
